import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { 
  Container, 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  IconButton,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardMedia,
  Badge,
  Chip,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { 
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  KeyboardArrowRight as ArrowRightIcon,
  ShoppingBag as BagIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import cartBn from '../i18n/bn/cart';
import cartEn from '../i18n/en/cart';
import cartNp from '../i18n/np/cart';
import cartPk from '../i18n/pk/cart';
import cartBt from '../i18n/bt/cart';
import cartDe from '../i18n/de/cart';
import cartSl from '../i18n/sl/cart';
import cartAf from '../i18n/af/cart';
import cartCn from '../i18n/cn/cart';
import { parsePrice, formatPriceForDisplay, formatPriceWithBengaliDigits } from '../utils/priceUtils';

const Cart = () => {
  const { isAuthenticated, user } = useAuth();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal, isLoading } = useCart();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentLanguage = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = currentLanguage === 'en' ? cartEn : 
            currentLanguage === 'np' ? cartNp : 
            currentLanguage === 'pk' ? cartPk : 
            currentLanguage === 'bt' ? cartBt : 
            currentLanguage === 'de' ? cartDe : 
            currentLanguage === 'sl' ? cartSl : 
            currentLanguage === 'af' ? cartAf : 
            currentLanguage === 'cn' ? cartCn : 
            cartBn;
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Format price function for Bengali digit display
  const formatPrice = (price) => {
    return formatPriceWithBengaliDigits(price, false); // Bengali digits, no currency symbol
  };

  // Format price for order summary with Bengali digits
  const formatPriceWithCurrency = (price) => {
    return formatPriceWithBengaliDigits(price, true); // Bengali digits with currency symbol
  };

  // Calculate cart totals
  const subtotal = getCartTotal();
  const deliveryFee = subtotal > 1000 ? 0 : 60;
  const discount = subtotal > 2000 ? subtotal * 0.05 : 0; // 5% discount on orders over 2000 Taka
  const total = subtotal + deliveryFee - discount;

  // Handle quantity change
  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(id);
    } else {
      updateQuantity(id, newQuantity);
    }
  };

  // Handle item removal
  const handleRemoveItem = (id) => {
    removeFromCart(id);
    setSnackbar({
      open: true,
      message: t.item.removed,
      severity: 'success'
    });
  };

  // Handle checkout navigation
  const handleProceedToCheckout = () => {
    // Temporarily commented out for design inspection
    // if (!isAuthenticated) {
    //   setSnackbar({
    //     open: true,
    //     message: currentLanguage === 'en' ? 'Please login to proceed to checkout' : 'অনুগ্রহ করে লগইন করুন চেকআউট করার জন্য',
    //     severity: 'warning'
    //   });
    //   navigate('/auth');
    //   return;
    // }
    
    navigate('/checkout');
  };

  // Handle clear cart
  const handleClearCart = () => {
    clearCart();
    setSnackbar({
      open: true,
      message: t.cleared,
      severity: 'info'
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Cart Item Component
  const CartItemCard = ({ item }) => {
    // Debug: Log item details
    console.log('CartItemCard - Item:', item.name, 'Price:', item.price, 'Type:', typeof item.price);
    console.log('CartItemCard - Parsed price:', parsePrice(item.price));
    console.log('CartItemCard - Formatted price:', formatPriceWithCurrency(item.price));
    
    return (
    <Card 
      sx={{ 
        mb: 2, 
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Product Image */}
          <Grid item xs={12} sm={3}>
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                height="120"
                image={item.image || '/api/placeholder/150/120'}
                alt={item.name}
                sx={{ 
                  borderRadius: 2,
                  objectFit: 'cover',
                  backgroundColor: '#f5f5f5'
                }}
              />
              <Chip
                label={t.item.inStock}
                color="success"
                size="small"
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} sm={5}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: '#2D3748',
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}
            >
              {item.name}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                {item.store?.charAt(0) || (currentLanguage === 'en' ? 'S' : 'দ')}
              </Avatar>
              {item.store || (currentLanguage === 'en' ? 'Store Name' : 'দোকানের নাম')}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: '1.2rem'
              }}
            >
              {formatPriceWithCurrency(item.price)}
            </Typography>
          </Grid>

          {/* Quantity Controls */}
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton 
                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                size="small"
                sx={{
                  backgroundColor: '#f7f7f7',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography 
                sx={{ 
                  mx: 2, 
                  minWidth: 40, 
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}
              >
                {item.quantity}
              </Typography>
              <IconButton 
                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                size="small"
                sx={{
                  backgroundColor: '#f7f7f7',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Total & Actions */}
          <Grid item xs={12} sm={2}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  mb: 1
                }}
              >
                {formatPriceWithCurrency(parsePrice(item.price) * item.quantity)}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <IconButton 
                  onClick={() => handleRemoveItem(item.id)}
                  size="small"
                  color="error"
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(211, 47, 47, 0.08)' 
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="primary">
                  <FavoriteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    );
  };

  // Order Summary Component
  const OrderSummary = () => (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3, 
        borderRadius: 3,
        border: '1px solid #e0e0e0',
        position: 'sticky',
        top: 100
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2D3748' }}>
        {t.orderSummary}
      </Typography>
      
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>{t.subtotal} ({cartItems.length} {currentLanguage === 'en' ? 'items' : 'আইটেম'}):</Typography>
          <Typography>{formatPriceWithCurrency(subtotal)}</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>{t.delivery}:</Typography>
          <Typography color={deliveryFee === 0 ? 'success.main' : 'text.primary'}>
            {deliveryFee === 0 ? t.free : formatPriceWithCurrency(deliveryFee)}
          </Typography>
        </Box>
        
        {discount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography color="success.main">{t.discount}:</Typography>
            <Typography color="success.main">-{formatPriceWithCurrency(discount)}</Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t.total}:</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {formatPriceWithCurrency(total)}
          </Typography>
        </Box>
      </Box>

      {/* Savings Message */}
          {deliveryFee === 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
          <Typography variant="body2" color="success.main" sx={{ textAlign: 'center' }}>
            <ShippingIcon sx={{ mr: 1, fontSize: 16 }} />
                {currentLanguage === 'en' ? 'You got free delivery!' : 'আপনি ফ্রি ডেলিভারি পেয়েছেন!'}
          </Typography>
        </Box>
      )}

      {/* Coupon Code */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t.couponPlaceholder}
          variant="outlined"
          sx={{ mb: 1 }}
        />
        <Button 
          variant="outlined" 
          fullWidth 
          size="small"
          sx={{ borderRadius: 2 }}
        >
          {t.applyCoupon}
        </Button>
      </Box>

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleProceedToCheckout}
        sx={{
          py: 1.5,
          borderRadius: 2,
          fontWeight: 600,
          fontSize: '1.1rem',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.35)',
          }
        }}
        endIcon={<ArrowRightIcon />}
      >
        {t.proceedCheckout}
      </Button>

      {/* Trust Indicators */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="success" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              {t.trust.securePayment}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShippingIcon color="primary" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              {t.trust.fastDelivery}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="info" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              {t.trust.cod}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );

  // Show loading state
  if (isLoading) {
    return (
      <>
        <Header />
        <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container 
        maxWidth="xl" 
        sx={{ 
          my: 4, 
          px: { xs: 2, sm: 3, md: 4 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                color: '#2D3748',
                fontSize: { xs: '1.8rem', sm: '2.2rem' }
              }}
            >
              <BagIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {t.title}
            </Typography>
            
            {cartItems.length > 0 && (
                <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearCart}
                startIcon={<ClearIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {t.clearAll}
              </Button>
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            {t.itemsInBag(cartItems.length)}
          </Typography>
        </Box>
        
        {cartItems.length === 0 ? (
          /* Empty Cart */
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper 
              sx={{ 
                p: 6, 
                textAlign: 'center',
                borderRadius: 3,
                border: '1px solid #e0e0e0',
                maxWidth: 600,
                width: '100%'
              }}
            >
            <CartIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 3 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#666' }}>
              {t.empty.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
              {t.empty.subtitle.split('\\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              component={Link}
              to="/bazars"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
              }}
              startIcon={<BagIcon />}
            >
              {t.empty.cta}
            </Button>
          </Paper>
          </Box>
        ) : (
          /* Cart with Items */
          <Grid container spacing={4}>
            {/* Cart Items */}
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t.sectionCartItems}
                </Typography>
                {cartItems.map((item) => (
                  <CartItemCard key={item.id} item={item} />
                ))}
              </Box>

              {/* Continue Shopping */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/bazars"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                  startIcon={<BagIcon />}
                >
                  {t.continueShopping}
                </Button>
              </Box>
            </Grid>

            {/* Order Summary */}
            <Grid item xs={12} md={4}>
              <OrderSummary />
            </Grid>
          </Grid>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
      <Footer />
    </Box>
  );
};

export default Cart;
